import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Chip, CircularProgress, IconButton, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import moment from 'moment';

import { getPodsState } from '../../../../features/pod/get_pods';
import { getStorePromotionSettingsState } from '../../../../features/price/get_store_promotion_settings';
import { MISC_DATE_ISO_TIME_ONLY_FORMAT } from '../../../../core/constants';
import { updateStorePromotionSettingStatusNotificationData } from '../../../../core/utils/notification_utils';
import { ApiRequestStatusEnum } from '../../../../core/enums/common/api';
import useNotification from '../../../../core/hooks/use_notification';
import { updateStorePromotionSettingStatusAction } from '../../../../features/price/update_store_promotion_setting_status';
import UpdateStorePromotionSettingDialog from './form/update_store_promotion_setting_dialog';

const StorePromotionSettingsList = ({ refreshList, type }) => {
    const dispatch = useDispatch();
    const { showNotification } = useNotification();

    const getPods = useSelector(getPodsState);
    const getStorePromotionSettings = useSelector(getStorePromotionSettingsState);

    const [updatingPodIds, setUpdatingPodIds] = useState({});

    const onUpdateStorePromotionSettingStatus = (podId, type) => {
        setUpdatingPodIds(prev => ({ ...prev, [podId]: true }));

        dispatch(updateStorePromotionSettingStatusAction({ podId, type }))
            .then(response => {
                showNotification(updateStorePromotionSettingStatusNotificationData(response));
                response?.meta?.requestStatus === ApiRequestStatusEnum.FULFILLED && refreshList();
            })
            .finally(() => {
                setUpdatingPodIds(prev => ({ ...prev, [podId]: false }));
            });
    };

    const getStorePromotionSetting = (podId, podName) => {
        const setting = getStorePromotionSettings?.data?.find(sps => sps?.podId === podId) || null;
        const isUpdating = updatingPodIds[podId] || false;

        const timeString = setting !== null ? moment(setting?.timeFrom, MISC_DATE_ISO_TIME_ONLY_FORMAT).format('HH:mm') : '00:00';
        const isActive = setting !== null ? setting?.active : false;

        return (
            <>
                <TableCell align="center">
                    <Typography variant="body2" sx={ { letterSpacing: 1 } }>
                        { timeString }
                    </Typography>
                </TableCell>
                <TableCell align="center">
                    <Chip
                        label={ isActive ? 'Aktywny' : 'Nieaktywny' }
                        color={ isActive ? 'success' : 'default' }
                        size="small"
                    />
                </TableCell>
                <TableCell align="right" sx={ { pr: 2 } }>
                    <Stack direction="row" spacing={ 1 } justifyContent="flex-end" alignItems="center">
                        <Tooltip title={ isActive ? 'Dezaktywuj' : 'Aktywuj' }>
                            <span>
                                <IconButton
                                    size="small"
                                    color={ isActive ? 'error' : 'success' }
                                    onClick={ () => onUpdateStorePromotionSettingStatus(podId, type) }
                                    disabled={ isUpdating }
                                >
                                    { isUpdating ? <CircularProgress size={ 20 } color="inherit"/> :
                                        <PowerSettingsNewIcon fontSize="small"/> }
                                </IconButton>
                            </span>
                        </Tooltip>

                        <UpdateStorePromotionSettingDialog refreshList={ refreshList } type={ type } podId={ podId }
                                                           podName={ podName }/>
                    </Stack>
                </TableCell>
            </>
        );
    };

    return (
        <TableContainer component={ Paper } variant="outlined">
            <Table stickyHeader size="small">
                <TableHead>
                    <TableRow sx={ { '& th': { backgroundColor: 'slate.50', fontWeight: 600 } } }>
                        <TableCell>Punkt sprzedaży</TableCell>
                        <TableCell align="center">Czas rozpoczęcia</TableCell>
                        <TableCell align="center">Status</TableCell>
                        <TableCell align="right" sx={ { pr: 3 } }></TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {
                        getPods?.data?.content?.map(pod => {
                            const podName = pod?.indoorPartner?.name + ' (' + pod?.description + ') - ' + (pod?.device?.serviceId || 'brak urządzenia');
                            return (
                                <TableRow key={ pod?.id } hover>
                                    <TableCell sx={ { fontWeight: 500 } }>
                                        { podName }
                                    </TableCell>
                                    {
                                        getStorePromotionSetting(pod?.id, podName)
                                    }
                                </TableRow>
                            );
                        })
                    }
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default StorePromotionSettingsList;